import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import React, { useEffect } from 'react'
import { Markdown } from 'tiptap-markdown'

import { Button } from '@/components/Button'
import { Flex } from '@/components/Flex'
import { Label } from '@/components/Label'
import './PreparationStepsEditor.css'

type Props = {
  content: string
  label?: React.ReactNode
  isDisabled?: boolean
  onUpdate: (content: string) => void
}

const normalizeMarkdown = (content: string) => {
  return (
    content
      // Fix headers: #title -> # title
      .replace(/^(#{1,6})([^\s#])/gm, '$1 $2')
      // Fix unordered lists: -item -> - item
      .replace(/^-([^\s])/gm, '1. $1')
      // Fix ordered lists: 1.item -> 1. item
      .replace(/^(\d+)\.([^\s])/gm, '$1. $2')
  )
}

export default function PreparationStepsEditor({ content, label, isDisabled, onUpdate }: Props) {
  const editor = useEditor({
    extensions: [StarterKit, Markdown],
    onUpdate: ({ editor }) => {
      onUpdate(editor.storage.markdown.getMarkdown())
    },
    editable: !isDisabled,
    // Normalize markdown content to handle legacy content. This may be removed later
    content: normalizeMarkdown(content),
  })

  useEffect(() => {
    if (editor && content !== editor.storage.markdown.getMarkdown()) {
      editor.commands.setContent(normalizeMarkdown(content))
    }
  }, [content, editor])

  if (!editor) {
    return null
  }

  return (
    <div className="PreparationStepsEditor">
      {label && <Label>{label}</Label>}

      <div
        className="PreparationStepsEditor-content"
        style={{
          backgroundColor: isDisabled ? '#f9fafb' : 'white',
        }}>
        <Flex gap={8} style={{ marginBottom: '8px' }}>
          <Button
            className="PreparationStepsEditor-button"
            type="button"
            isDisabled={isDisabled}
            onPress={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            data-active={editor.isActive('heading', { level: 2 })}>
            Heading
          </Button>

          <Button
            type="button"
            className="PreparationStepsEditor-button"
            isDisabled={isDisabled}
            onPress={() => editor.chain().focus().toggleOrderedList().run()}
            data-active={editor.isActive('orderedList')}>
            1. List
          </Button>
        </Flex>
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}
